import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import moment from 'moment';

import { IFakturowniaInvoiceCorrection } from 'core/hooks';
import { MoneyCell } from 'molecules';
import { PATHS } from 'router/config';

import * as S from './FakturowniaInvoices.styled';

interface ICorrectionsListProps {
  /**
   * Phase 7: corrections come from the parent invoice's `corrections`
   * reverse-relation — no separate query. Pass `[]` to hide the card.
   */
  corrections: IFakturowniaInvoiceCorrection[];
}

/**
 * Corrections that adjust the current invoice. Hidden when there are none so
 * the detail page stays compact for the common case (TZ §4.5). The reason
 * column shows the actual `correctionReason` now that the detail projection
 * carries it for each child (Phase 7 GraphQL change).
 */
export const CorrectionsList = memo(
  ({ corrections }: ICorrectionsListProps) => {
    const { t } = useTranslation();
    const history = useHistory();

    if (corrections.length === 0) return null;

    return (
      <S.Card>
        <S.CardTitle>
          {t('pages.fakturownia.invoice.correctionsSection', {
            count: corrections.length,
          })}
        </S.CardTitle>
        <S.Table>
          <thead>
            <tr>
              <th>{t('pages.fakturownia.invoices.columns.number')}</th>
              <th>{t('pages.fakturownia.invoices.columns.date')}</th>
              <th style={{ textAlign: 'right' }}>
                {t('pages.fakturownia.invoices.columns.gross')}
              </th>
              <th>{t('pages.fakturownia.invoice.correctionReason')}</th>
            </tr>
          </thead>
          <tbody>
            {corrections.map((row) => (
              <tr
                key={row.id}
                onClick={() =>
                  history.push(PATHS.financesInvoice.replace(':id', row.id))
                }
              >
                <S.NameCell>{row.number}</S.NameCell>
                <td style={{ color: '#64748b', whiteSpace: 'nowrap' }}>
                  {row.issueDate
                    ? moment(row.issueDate).format('DD.MM.YYYY')
                    : '—'}
                </td>
                <td>
                  <MoneyCell
                    amount={row.priceGross}
                    currency={row.currency}
                    negative
                  />
                </td>
                <td style={{ color: '#475569' }}>
                  {row.correctionReason || '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </S.Table>
      </S.Card>
    );
  },
);
